import { FadeUp, SectionTag } from "../components/motion";
import { TECH_STACK, IMAGES } from "../data/content";
import useSEO from "../hooks/useSEO";
import { GitBranch, RefreshCcw, Layers3, FileCheck } from "lucide-react";

const FRAMEWORK = [
  { icon: GitBranch, title: "Methodology", desc: "Project execution methodology based on SDLC, with Agile and Rational Unified Methodology applied where the engagement demands it." },
  { icon: Layers3, title: "Infrastructure", desc: "Delivery infrastructure — environments, tooling and test rigs — supporting the methodology at every stage." },
  { icon: RefreshCcw, title: "Project Management", desc: "Structured project ownership with milestone governance, transparent reporting and single-point accountability." },
  { icon: FileCheck, title: "Quality", desc: "A documented Quality Management System ensuring software and IT services consistently conform to specified requirements." },
];

export default function Technology() {
  useSEO(
    "Technology & Methodology — SDLC, Agile, Networking, Surveillance | SGSNet Patna",
    "The technology stack behind SATI Global Systems (SGSNet): web & application development, database integration, servers, storage, data centre, LAN/WAN networking, CCTV, DVR/NVR, IP cameras, SDLC, Agile and Rational Unified Methodology."
  );
  return (
    <main data-testid="technology-page" className="pt-16 sm:pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp><SectionTag>Technology & Methodology</SectionTag></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08] max-w-3xl">
            The stack behind <span className="text-blue-600">the standard.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Multidisciplinary engineering across software, infrastructure, networking and
            surveillance — unified by disciplined delivery methodology.
          </p>
        </FadeUp>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TECH_STACK.map((cat, i) => (
            <FadeUp key={cat.title} delay={(i % 3) * 0.08}>
              <div className="bg-panel border border-slate-200 rounded-2xl p-7 hover:border-blue-600/40 transition-colors h-full" data-testid={`tech-category-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-600">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-2 font-heading text-xl font-semibold text-slate-900">{cat.title}</h2>
                <ul className="mt-5 space-y-2.5">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-center gap-2.5 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 rotate-45 bg-blue-600/70 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.2}>
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 min-h-[240px] sm:col-span-2 lg:col-span-1">
              <img src={IMAGES.fiber} alt="Fiber optic infrastructure" className="absolute inset-0 w-full h-full object-cover spotlight-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/90 to-transparent flex items-end p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-700">Fiber · Servers · Signal</p>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="mt-24">
          <FadeUp><SectionTag>Project Execution Framework</SectionTag></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">How we execute</h2>
          </FadeUp>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FRAMEWORK.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.08}>
                <div className="bg-panel border border-slate-200 rounded-xl p-6 h-full hover:border-blue-600/40 transition-colors" data-testid={`framework-card-${i}`}>
                  <f.icon size={22} className="text-blue-600" strokeWidth={1.6} />
                  <h3 className="mt-4 font-heading font-semibold text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-[13px] text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
